import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";
import VideoUpload from "@/components/video/VideoUpload/VideoUpload";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";
import cls from "./AdminCitySingle.module.scss";

const LANGS = ["en", "si", "ta", "hi"];
const STATUSES = ["open", "closed"];

const languageLabel = (code) => {
  const map = {
    en: "English",
    si: "සිංහල (Sinhala)",
    ta: "தமிழ் (Tamil)",
    hi: "हिन्दी (Hindi)",
  };
  return map[code] || code?.toUpperCase() || "";
};

export default function AdminCitySingle() {
  const { cityId } = useParams();
  const navigate = useNavigate();

  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  const [form, setForm] = useState({
    cityLanguage: "en",
    cityName: "",
    cityStatus: "open",
    cityVibe: "",
    cityIntro: "",
    cityDescription: "",
    sortOrder: 0,
    isFeatured: false,
    tags: "",
    cityVideoId: "",
  });

  const [err, setErr] = useState("");
  const [saveMsg, setSaveMsg] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadErr, setUploadErr] = useState("");

  const [imageFiles, setImageFiles] = useState({
    avatar: null,
    avatarThumb: null,
    cover: null,
    mrNoty: null,
    mrsNoty: null,
  });

  const [imagePreviews, setImagePreviews] = useState({
    avatar: null,
    avatarThumb: null,
    cover: null,
    mrNoty: null,
    mrsNoty: null,
  });

  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const hydrateForm = (doc) => {
    setForm({
      cityLanguage: doc.cityLanguage || "en",
      cityName: doc.cityName || "",
      cityStatus: doc.cityStatus || "open",
      cityVibe: doc.cityVibe || "",
      cityIntro: doc.cityIntro || "",
      cityDescription: doc.cityDescription || "",
      sortOrder: typeof doc.sortOrder === "number" ? doc.sortOrder : 0,
      isFeatured: Boolean(doc.isFeatured),
      tags: Array.isArray(doc.tags) ? doc.tags.join(", ") : "",
      cityVideoId: doc.cityVideoId?._id || doc.cityVideoId || "",
    });
  };

  const fetchCity = useCallback(async () => {
    if (!cityId) return;
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get(`/v1/admin/cities/${cityId}`);
      setCity(data || null);
      if (data) hydrateForm(data);
      // Clear local previews on refresh
      setImagePreviews({
        avatar: null,
        avatarThumb: null,
        cover: null,
        mrNoty: null,
        mrsNoty: null,
      });
      setImageFiles({
        avatar: null,
        avatarThumb: null,
        cover: null,
        mrNoty: null,
        mrsNoty: null,
      });
    } catch (e) {
      console.error("Load city failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Could not load city.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    fetchCity();
  }, [fetchCity]);

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!cityId) return;
    setSaving(true);
    setSaveMsg("");
    setErr("");
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder) || 0,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      const { data } = await api.patch(`/v1/admin/cities/${cityId}`, payload);
      setCity(data || null);
      if (data) hydrateForm(data);
      setSaveMsg("City details saved.");
    } catch (e) {
      console.error("Update city failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Failed to update city.";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (key, file) => {
    setImageFiles((prev) => ({ ...prev, [key]: file || null }));
    if (file) {
      const url = URL.createObjectURL(file);
      setImagePreviews((prev) => ({ ...prev, [key]: url }));
    } else {
      setImagePreviews((prev) => ({ ...prev, [key]: null }));
    }
  };

  const doUpload = async (key, path) => {
    setUploadMsg("");
    setUploadErr("");
    const file = imageFiles[key];
    if (!file) {
      setUploadErr("Pick a file first.");
      return;
    }
    try {
      setUploadingKey(key);
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const contentType = file.type || "application/octet-stream";

      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/presign/${path}`,
        { ext, contentType },
      );

      const { uploadUrl, processedKey } = data || {};
      if (!uploadUrl) throw new Error("Invalid presign response");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });

      if (!putRes.ok) {
        console.error("S3 upload failed", putRes.status, await putRes.text());
        throw new Error("Failed to upload to storage.");
      }

      setUploadMsg(
        `${key} uploaded. Processed key saved as ${
          processedKey || "(pending)"
        }.`,
      );

      // Refresh city to get fresh signed URLs
      await fetchCity();
    } catch (e) {
      console.error("Upload failed", e);
      const msg = e?.response?.data?.message || e?.message || "Upload failed.";
      setUploadErr(msg);
    } finally {
      setUploadingKey("");
    }
  };

  const attachVideoId = async (videoId) => {
    if (!videoId) {
      setUploadErr("Video ID is required to attach.");
      return;
    }
    try {
      setUploadErr("");
      await api.patch(`/v1/admin/cities/${cityId}/video`, { videoId });
      await fetchCity();
      setUploadMsg(`Video ${videoId} attached.`);
    } catch (e) {
      console.error("Attach video failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Could not attach video.";
      setUploadErr(msg);
    }
  };

  const detachVideo = async () => {
    try {
      setUploadErr("");
      await api.patch(`/v1/admin/cities/${cityId}`, { cityVideoId: null });
      await fetchCity();
      setUploadMsg("Video removed.");
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || "Could not remove video.";
      setUploadErr(msg);
    }
  };

  // Main avatar preview (prioritize thumb, then avatar, then local overrides)
  const mainAvatarUrl = useMemo(() => {
    if (imagePreviews.avatarThumb) return imagePreviews.avatarThumb;
    if (city?.avatarThumb?.url) return city.avatarThumb.url;
    if (imagePreviews.avatar) return imagePreviews.avatar;
    if (city?.avatar?.url) return city.avatar.url;
    return null;
  }, [imagePreviews, city]);

  const slotUrl = (slot) => {
    if (imagePreviews[slot]) return imagePreviews[slot];
    if (slot === "avatarThumb") return city?.avatarThumb?.url || null;
    if (slot === "avatar") return city?.avatar?.url || null;
    if (slot === "cover") return city?.cover?.url || null;
    if (slot === "mrNoty") return city?.mrNoty?.url || null;
    if (slot === "mrsNoty") return city?.mrsNoty?.url || null;
    return null;
  };

  if (loading && !city) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>City</h1>
          <p className={cls.subtitle}>Loading city details…</p>
        </div>
        <div className={cls.skeleton} />
      </div>
    );
  }

  if (!city) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>City</h1>
          <p className={cls.subtitle}>No city found for this ID.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>
        <h1 className={cls.title}>City Settings</h1>
        <p className={cls.subtitle}>
          Admin view to review and update city details, images, and video.
        </p>
      </div>

      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>City ID</span>
          <span className={cls.metaValue}>{city._id}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Language</span>
          <span className={cls.metaValue}>
            {languageLabel(city.cityLanguage || form.cityLanguage)}
          </span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{city.cityStatus || "open"}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Created</span>
          <span className={cls.metaValue}>
            {city.createdAt ? new Date(city.createdAt).toLocaleString() : "—"}
          </span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {saveMsg && <div className={cls.success}>{saveMsg}</div>}
      {uploadMsg && <div className={cls.success}>{uploadMsg}</div>}
      {uploadErr && <div className={cls.error}>{uploadErr}</div>}

      <div className={cls.layout}>
        {/* LEFT COLUMN – IMAGES / AVATAR */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>City Images</h2>
            <p className={cls.cardSubtitle}>
              Preview and upload the main avatar, thumb, cover, and Noty art.
            </p>
          </div>

          {/* Main avatar bubble (uses thumb if available) */}
          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              {mainAvatarUrl ? (
                <img
                  src={mainAvatarUrl}
                  alt="City avatar"
                  className={cls.avatarImage}
                />
              ) : (
                <div className={cls.avatarFallback}>No avatar</div>
              )}
            </div>
            {city.cityAvatarUpdatedAt && (
              <p className={cls.avatarMeta}>
                Avatar updated{" "}
                {new Date(city.cityAvatarUpdatedAt).toLocaleString()}
              </p>
            )}
          </div>

          {/* Individual image slots */}
          <div className={cls.imagesGrid}>
            {/* Avatar (Full) */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar (poster.webp)</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("avatar") ? (
                  <img
                    src={slotUrl("avatar")}
                    alt="Avatar"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No avatar</div>
                )}
              </div>
              <p className={cls.imageHint}>
                High-quality avatar used for large previews and hero sections.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose avatar</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("avatar", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.avatar && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.avatar.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("avatar", "avatar")}
                disabled={uploadingKey === "avatar" || !imageFiles.avatar}
              >
                {uploadingKey === "avatar" ? "Uploading…" : "Upload Avatar"}
              </button>
            </div>

            {/* Avatar Thumb */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar Thumb (480.webp)</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("avatarThumb") ? (
                  <img
                    src={slotUrl("avatarThumb")}
                    alt="Avatar thumb"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No thumb</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Used for small spots like cards & lists.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose thumb</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("avatarThumb", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.avatarThumb && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.avatarThumb.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("avatarThumb", "avatar-thumb")}
                disabled={
                  uploadingKey === "avatarThumb" || !imageFiles.avatarThumb
                }
              >
                {uploadingKey === "avatarThumb" ? "Uploading…" : "Upload Thumb"}
              </button>
            </div>

            {/* Cover */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Cover Image</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("cover") ? (
                  <img
                    src={slotUrl("cover")}
                    alt="Cover"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No cover</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Hero background on city landing sections.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose cover</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("cover", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.cover && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.cover.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("cover", "cover")}
                disabled={uploadingKey === "cover" || !imageFiles.cover}
              >
                {uploadingKey === "cover" ? "Uploading…" : "Upload Cover"}
              </button>
            </div>

            {/* Mr Noty */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mr Noty Image</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("mrNoty") ? (
                  <img
                    src={slotUrl("mrNoty")}
                    alt="Mr Noty"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No Mr Noty</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Character art for the city&apos;s male persona.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose Mr Noty</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("mrNoty", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.mrNoty && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.mrNoty.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("mrNoty", "mr-noty")}
                disabled={uploadingKey === "mrNoty" || !imageFiles.mrNoty}
              >
                {uploadingKey === "mrNoty" ? "Uploading…" : "Upload Mr Noty"}
              </button>
            </div>

            {/* Mrs Noty */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mrs Noty Image</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("mrsNoty") ? (
                  <img
                    src={slotUrl("mrsNoty")}
                    alt="Mrs Noty"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No Mrs Noty</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Character art for the city&apos;s female persona.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose Mrs Noty</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("mrsNoty", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.mrsNoty && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.mrsNoty.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("mrsNoty", "mrs-noty")}
                disabled={uploadingKey === "mrsNoty" || !imageFiles.mrsNoty}
              >
                {uploadingKey === "mrsNoty" ? "Uploading…" : "Upload Mrs Noty"}
              </button>
            </div>
          </div>
        </section>

        {/* RIGHT COLUMN – CITY FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>City Details</h2>
            <p className={cls.cardSubtitle}>
              Name, language, status, vibe, intro, description, and flags.
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSave}>
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityLanguage">
                  Language
                </label>
                <select
                  id="cityLanguage"
                  className={cls.select}
                  value={form.cityLanguage}
                  onChange={(e) => setField("cityLanguage", e.target.value)}
                >
                  {LANGS.map((l) => (
                    <option key={l} value={l}>
                      {l.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityStatus">
                  Status
                </label>
                <select
                  id="cityStatus"
                  className={cls.select}
                  value={form.cityStatus}
                  onChange={(e) => setField("cityStatus", e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityName">
                  City name
                </label>
                <input
                  id="cityName"
                  type="text"
                  className={cls.input}
                  value={form.cityName}
                  onChange={(e) => setField("cityName", e.target.value)}
                  placeholder="Party Pulse City"
                />
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="sortOrder">
                  Sort order
                </label>
                <input
                  id="sortOrder"
                  type="number"
                  className={cls.input}
                  value={form.sortOrder}
                  onChange={(e) => setField("sortOrder", e.target.value)}
                />
                <p className={cls.help}>
                  Lower numbers appear earlier in lists.
                </p>
              </div>
            </div>

            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityVibe">
                  Vibe / tagline
                </label>
                <input
                  id="cityVibe"
                  type="text"
                  className={cls.input}
                  value={form.cityVibe}
                  onChange={(e) => setField("cityVibe", e.target.value)}
                  placeholder="After-dark energy, party-first crowd…"
                />
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="isFeatured">
                  Featured city
                </label>
                <div className={cls.checkboxRow}>
                  <input
                    id="isFeatured"
                    type="checkbox"
                    checked={form.isFeatured}
                    onChange={(e) => setField("isFeatured", e.target.checked)}
                  />
                  <span className={cls.help}>
                    Show in featured carousels and hero rows.
                  </span>
                </div>
              </div>
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="cityIntro">
                Intro
              </label>
              <textarea
                id="cityIntro"
                rows={3}
                className={cls.input}
                value={form.cityIntro}
                onChange={(e) => setField("cityIntro", e.target.value)}
                placeholder="Short intro shown on the first fold."
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="cityDescription">
                Description
              </label>
              <textarea
                id="cityDescription"
                rows={4}
                className={cls.input}
                value={form.cityDescription}
                onChange={(e) => setField("cityDescription", e.target.value)}
                placeholder="Longer description when someone opens the city."
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="tags">
                Tags (comma separated)
              </label>
              <input
                id="tags"
                type="text"
                className={cls.input}
                value={form.tags}
                onChange={(e) => setField("tags", e.target.value)}
                placeholder="party, nightlife, music"
              />
            </div>

            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={fetchCity}
                disabled={saving}
              >
                Reset / Refresh
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>

      {/* VIDEO CARD */}
      <section className={cls.formCard}>
        <div className={cls.formHeader}>
          <h2 className={cls.cardTitle}>City Video</h2>
          <p className={cls.cardSubtitle}>
            Attach an existing video, or upload a fresh one for this city.
          </p>
        </div>

        <div className={cls.videoRow}>
          <div className={cls.field}>
            <label className={cls.label} htmlFor="cityVideoId">
              Current video ID
            </label>
            <input
              id="cityVideoId"
              type="text"
              className={cls.input}
              value={form.cityVideoId}
              onChange={(e) => setField("cityVideoId", e.target.value)}
              placeholder="Video ObjectId"
            />
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.primaryBtn}
                onClick={() => attachVideoId(form.cityVideoId)}
              >
                Attach Video
              </button>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={detachVideo}
              >
                Remove Video
              </button>
            </div>
          </div>

          <div className={cls.preview}>
            {form.cityVideoId ? (
              <VideoPlayer
                token={token}
                videoId={form.cityVideoId}
                mode="proxy"
                height={220}
              />
            ) : (
              <div className={cls.noVideo}>No video attached.</div>
            )}
          </div>
        </div>

        <div className={cls.uploadBlock}>
          <VideoUpload
            defaultLang={form.cityLanguage}
            onDone={(res) => attachVideoId(res?.videoId)}
            showUploader
          />
        </div>
      </section>
    </div>
  );
}
